/**
 * Print stack (snippets/print-stack.liquid, assets/print-stack.css): the
 * product's media as a pile of prints, shuffled through.
 *
 * Over the pile lies a transparent scroller, a snap stop to a print, in
 * several rounds of the pile and started in the middle, so it can be
 * shuffled dozens of times either way. Between two stops one print is on
 * the move, as far as the scroll has gone: going on, the top print is
 * swiped off the screen to the left, uncovering the next; going back, the
 * last one swiped away comes back from the left onto the pile. A print
 * swiped away rejoins the pile at the bottom, out of sight, so after the
 * last print the first is under it again. Nothing goes out past the
 * screen's right edge, which would widen the page under the finger.
 * Swipes, trackpads, keys and momentum are the browser's; a mouse drags as
 * a finger does, and a tap or click takes the next print.
 *
 * Choosing a variant with a photo of its own (not the last one's)
 * shuffles to that photo, the short way round, as if the shopper had
 * swiped there (a reader who asks for reduced motion gets it at once); a
 * variant chosen in the URL starts the pile at its photo (data-start, from
 * the snippet).
 *
 * The top print's video plays while the pile is on screen, unless the
 * section's autoplay is off, the connection saves data or the reader asks
 * for reduced motion. Deeper prints' photos load as they near the top.
 *
 * iOS (AGENTS.md): the scroll position is never moved while a finger may
 * hold the scroller (only off screen, stackRebase), and the scroller's
 * width stays well under 100,000px.
 */

(() => {
  if (customElements.get("print-stack")) return;

  /* How many prints from the top have edges that show. */
  const STACK_SHOWN = 4;
  /* About how many snap stops the scroller holds, a few hundred pixels
     each. */
  const STACK_STOPS = 110;
  const stackConnection =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection;
  const stackSavesData =
    stackConnection &&
    (stackConnection.saveData ||
      /(^|\W)(2g|slow-2g|3g)$/.test(stackConnection.effectiveType || ""));
  const stackReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  );

  const stackMod = (n, m) => ((n % m) + m) % m;

  /* Every photo the pile has asked for, loaded or failed. */
  const stackPhotosIn = (s) =>
    Promise.all(
      s.prints
        .flatMap((print) => Array.from(print.querySelectorAll("img[src]")))
        .filter((img) => !img.complete)
        .map(
          (img) =>
            new Promise((resolve) => {
              img.addEventListener("load", resolve, { once: true });
              img.addEventListener("error", resolve, { once: true });
            })
        )
    );

  const stackLoad = (print) =>
    print.querySelectorAll("[data-src], [data-poster]").forEach((el) => {
      ["srcset", "poster", "src"].forEach((name) => {
        if (!el.dataset[name]) return;
        el.setAttribute(name, el.dataset[name]);
        delete el.dataset[name];
      });
    });

  /* The top print's video plays, while the pile is on screen, once the
     pile's photos are in (s.ready); the rest wait. Playing is what fetches
     a video (preload="none"), and a product's runs to megabytes, which
     fetched beside the photos held them back. */
  const stackPlay = (s) =>
    s.prints.forEach((print, i) => {
      const video = print.querySelector("video");
      if (!video) return;
      const plays =
        i === s.top &&
        s.visible &&
        s.ready &&
        s.autoplay &&
        !stackReducedMotion.matches;
      if (!plays) return video.pause();
      video.play().catch((error) => console.debug("print-stack:", error));
    });

  /* A print moved dx along the table, swinging a little as it goes. */
  const stackPose = (print, dx) => {
    const { tilt, x, y } = print.dataset;
    return `translate(${Number(x) + dx}px, ${y}px) rotate(${Number(tilt) + dx * 0.008}deg)`;
  };

  /* Off the screen to the left, from where the pile lies, at whatever
     tilt: the pile's right edge and a margin. */
  const stackOff = (s) => {
    const { right, width } = s.scroller.getBoundingClientRect();
    return right + width * 0.1 + 24;
  };

  /* The pile at the scroller's position. The moving print is the top at
     the stop before the scroll (from), as far off to the left as the
     scroll is past it (away); the pile under it starts at the next. The
     top, whose video plays, is whichever of the two shows more. */
  const stackDraw = (s) => {
    const n = s.prints.length;
    const at = s.scroller.scrollLeft / s.step;
    if (Math.abs(at - Math.round(at)) < 0.005) s.origin = Math.round(at);
    else if (Math.abs(at - s.origin) >= 1)
      s.origin = at > s.origin ? Math.floor(at) : Math.ceil(at);
    const on = at >= s.origin;
    const part = Math.abs(at - s.origin) < 0.005 ? 0 : Math.abs(at - s.origin);
    const from = on ? s.origin : s.origin - 1;
    const away = on ? part : 1 - part;
    const moving = part > 0 ? stackMod(from, n) : -1;
    const under = stackMod(part > 0 ? from + 1 : s.origin, n);
    const top = stackMod(away >= 0.5 ? from + 1 : from, n);
    /* Measured as a move begins, so the pile's place is current. */
    s.off = part > 0 ? s.off || stackOff(s) : 0;
    s.prints.forEach((print, i) => {
      const moves = i === moving;
      const depth = stackMod(i - under, n);
      print.style.zIndex = moves ? n + 1 : n - depth;
      print.style.transform = moves ? stackPose(print, -s.off * away) : "";
      print.classList.toggle("is-deep", depth >= STACK_SHOWN && !moves);
      print.setAttribute("aria-hidden", i !== top);
      if (depth < STACK_SHOWN || depth === n - 1 || moves) stackLoad(print);
    });
    if (top === s.top) return;
    s.top = top;
    stackPlay(s);
  };

  /* iOS can let a fast fling come to rest between two stops, a print
     half out; the nearest stop takes it, once no finger or mouse holds
     it. */
  const stackSettle = (s) => {
    const at = s.scroller.scrollLeft / s.step;
    if (s.touching || s.scroller.classList.contains("is-dragging")) return;
    if (Math.abs(at - Math.round(at)) < 0.005) return;
    s.scroller.scrollTo({ left: Math.round(at) * s.step, behavior: "smooth" });
  };

  /* A mouse drags the pile as a finger does, which the browser's own
     scrolling doesn't: the snap let go while it's held, then on to the
     stop it was dragged toward, or back if it barely moved. */
  const stackDragStart = (s, event) => {
    s.dragged = false;
    if (event.pointerType !== "mouse" || event.button !== 0) return;
    const from = Math.round(s.scroller.scrollLeft / s.step);
    s.drag = { x: event.clientX, left: s.scroller.scrollLeft, from };
  };

  const stackDragMove = (s, event) => {
    if (!s.drag) return;
    const dx = event.clientX - s.drag.x;
    if (!s.dragged && Math.abs(dx) < 5) return;
    if (!s.dragged) s.scroller.setPointerCapture(event.pointerId);
    s.dragged = true;
    s.scroller.classList.add("is-dragging");
    s.scroller.scrollLeft = s.drag.left - dx;
  };

  const stackDragEnd = (s) => {
    if (!s.drag) return;
    const { from } = s.drag;
    s.drag = null;
    if (!s.dragged) return;
    const at = s.scroller.scrollLeft / s.step;
    const to =
      Math.abs(at - from) < 0.15
        ? from
        : at > from
          ? Math.ceil(at - 0.15)
          : Math.floor(at + 0.15);
    const release = () => !s.drag && s.scroller.classList.remove("is-dragging");
    s.scroller.addEventListener("scrollend", release, {
      once: true,
      signal: s.signal
    });
    setTimeout(release, 900);
    s.scroller.scrollTo({ left: to * s.step, behavior: "smooth" });
  };

  /* Back to the middle of the run, where nothing changes but the scroll
     position, and only while the pile is off screen, so no finger can be
     holding the scroller. */
  const stackRebase = (s) => {
    if (Math.abs(s.origin - s.home) < s.prints.length * 2) return;
    s.origin = s.home + stackMod(s.origin, s.prints.length);
    s.scroller.scrollLeft = s.origin * s.step;
  };

  /* To a variant's photo, the short way round, from the stop nearest where
     the pile is now (mid-shuffle, the scroll is redirected there). */
  const stackShow = (s, mediaId) => {
    const n = s.prints.length;
    const to = s.prints.findIndex(
      (print) => print.dataset.mediaId === String(mediaId)
    );
    if (to < 0 || s.drag || s.touching) return;
    const at = Math.round(s.scroller.scrollLeft / s.step);
    const ahead = stackMod(to - at, n);
    const steps = ahead > n / 2 ? ahead - n : ahead;
    if (!steps) return;
    s.scroller.scrollTo({
      left: (at + steps) * s.step,
      behavior: stackReducedMotion.matches ? "auto" : "smooth"
    });
  };

  /* A new width: the same stop, at the new step. */
  const stackResize = (s) => {
    const width = s.scroller.firstElementChild?.getBoundingClientRect().width;
    if (!width || width === s.step) return;
    s.step = width;
    s.scroller.scrollLeft = s.origin * s.step;
    stackDraw(s);
  };

  const stackListen = (s) => {
    const { scroller, signal } = s;
    const on = (type, handler, options = {}) =>
      scroller.addEventListener(type, handler, { signal, ...options });
    on(
      "scroll",
      () => {
        cancelAnimationFrame(s.frame);
        s.frame = requestAnimationFrame(() => stackDraw(s));
      },
      { passive: true }
    );
    ["touchstart", "touchend", "touchcancel"].forEach((type) =>
      on(type, (event) => (s.touching = event.touches.length), {
        passive: true
      })
    );
    if ("onscrollend" in scroller) on("scrollend", () => stackSettle(s));
    else
      on(
        "scroll",
        () => {
          clearTimeout(s.resting);
          s.resting = setTimeout(() => stackSettle(s), 150);
        },
        { passive: true }
      );
    on("pointerdown", (event) => stackDragStart(s, event));
    on("pointermove", (event) => stackDragMove(s, event));
    on("pointerup", () => stackDragEnd(s));
    on("pointercancel", () => stackDragEnd(s));
    on("click", () => {
      if (!s.dragged) scroller.scrollBy({ left: s.step, behavior: "smooth" });
    });
    /* The product's own form, in the pile's section: a quick view's
       elsewhere on the page is another product's. A variant sharing the
       last one's photo (a size, under one photo for them all) leaves the
       pile where the shopper put it. */
    const section = s.element.closest(".shopify-section");
    document.addEventListener(
      "variant:changed",
      (event) => {
        const media = event.detail?.variant?.featured_media;
        const was = event.detail?.previousVariant?.featured_media;
        if (!media || media.id === was?.id) return;
        if (event.target.closest?.(".shopify-section") !== section) return;
        stackShow(s, media.id);
      },
      { signal }
    );
  };

  const stackSetup = (element) => {
    const controller = new AbortController();
    const prints = Array.from(element.querySelectorAll(".instant"));
    const scroller = element.querySelector(".prints__scroller");
    const s = {
      element,
      prints,
      scroller,
      signal: controller.signal,
      autoplay: element.hasAttribute("autoplay-video") && !stackSavesData,
      visible: false,
      ready: false,
      top: 0,
      step: 1,
      touching: 0,
      drag: null,
      dragged: false,
      frame: 0,
      resting: 0,
      off: 0
    };
    const seen = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          s.visible = entry.intersectionRatio >= 0.5;
          stackPlay(s);
          /* A lone print has no run to come back to (no s.home). */
          if (s.home !== undefined && !entry.isIntersecting) stackRebase(s);
        }),
      { threshold: [0, 0.5] }
    );
    seen.observe(element);
    stackPhotosIn(s).then(() => {
      if (controller.signal.aborted) return;
      s.ready = true;
      stackPlay(s);
    });
    stackReducedMotion.addEventListener("change", () => stackPlay(s), {
      signal: controller.signal
    });
    /* A lone print (a product whose one media, or whose video after its
       dropped still, is all there is) is the top one: its video loads, to
       play once the pile is on screen. */
    if (!scroller || prints.length < 2) {
      prints.forEach(stackLoad);
      return () => {
        controller.abort();
        seen.disconnect();
      };
    }
    /* Some rounds of the pile, an odd number, so the middle is a round's
       start. */
    const rounds = Math.max(
      3,
      Math.floor(STACK_STOPS / prints.length / 2) * 2 + 1
    );
    s.home = Math.floor(rounds / 2) * prints.length;
    s.origin =
      s.home + stackMod(Number(element.dataset.start) || 0, prints.length);
    scroller.innerHTML = "<div></div>".repeat(rounds * prints.length + 1);
    s.step = scroller.firstElementChild.getBoundingClientRect().width || 1;
    scroller.scrollLeft = s.origin * s.step;
    stackListen(s);
    const resizing = new ResizeObserver(() => stackResize(s));
    resizing.observe(scroller);
    stackDraw(s);
    return () => {
      controller.abort();
      seen.disconnect();
      resizing.disconnect();
      cancelAnimationFrame(s.frame);
      clearTimeout(s.resting);
    };
  };

  class PrintStack extends HTMLElement {
    connectedCallback() {
      try {
        this.teardown = stackSetup(this);
      } catch (error) {
        console.error("print-stack:", error);
      }
    }

    disconnectedCallback() {
      this.teardown?.();
      this.teardown = null;
    }
  }

  customElements.define("print-stack", PrintStack);
})();
